import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Button, Alert, Table, Form, Badge, Spinner, Row, Col } from 'react-bootstrap';
import { userAPI } from '../api/userAPI';
import { permissionsAPI } from '../api/permissionsAPI';
import { usePermissions } from '../contexts/PermissionContext';

function UserPermissionManagementScreen() {
    const { hasPermission: hasCurrentUserPermission, loading: permissionsLoading } = usePermissions();
    const { userId } = useParams();
    const navigate = useNavigate();

    const [user, setUser] = useState(null);
    const [allPermissions, setAllPermissions] = useState([]);
    const [userPermissions, setUserPermissions] = useState([]);
    const [selectedPermissions, setSelectedPermissions] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [alert, setAlert] = useState(null);
    const [groupedPermissions, setGroupedPermissions] = useState({});

    useEffect(() => {
        loadData();
    }, [userId]);

    const loadData = async () => {
        try {
            setLoading(true);
            setAlert(null);

            const [userData, allPermsData, userPermsData] = await Promise.all([
                userAPI.getById(userId),
                permissionsAPI.getAllPermissions(),
                permissionsAPI.getUserPermissions(userId)
            ]);

            setUser(userData);
            setAllPermissions(allPermsData);
            setUserPermissions(userPermsData.permissions || []);

            // Group permissions by area (first part of code before the dot)
            const grouped = {};
            allPermsData.forEach(perm => {
                const area = perm.code.split('.')[0] || 'other';
                if (!grouped[area]) {
                    grouped[area] = [];
                }
                grouped[area].push(perm);
            });
            setGroupedPermissions(grouped);

        } catch (error) {
            setAlert({ variant: 'danger', message: 'Failed to load data' });
        } finally {
            setLoading(false);
        }
    };

    const hasPermission = (permissionCode) => {
        return userPermissions.some(p => p.endpoint_permission?.code === permissionCode);
    };

    const handleTogglePermission = (permissionCode) => {
        const newSelected = new Set(selectedPermissions);
        if (newSelected.has(permissionCode)) {
            newSelected.delete(permissionCode);
        } else {
            newSelected.add(permissionCode);
        }
        setSelectedPermissions(newSelected);
    };

    const handleGrantViewAll = (area) => {
        const areaPermissions = groupedPermissions[area] || [];
        const newSelected = new Set(selectedPermissions);

        // Get all view permissions in this area
        const viewPerms = areaPermissions.filter(perm => perm.code.includes('.view'));

        // Check if all view perms will be checked (granted or marked for grant)
        const allWillBeChecked = viewPerms.every(perm => {
            const isGranted = hasPermission(perm.code);
            const isSelected = newSelected.has(perm.code);
            // Will be checked if: granted and not marked for revoke, OR not granted and marked for grant
            return (isGranted && !isSelected) || (!isGranted && isSelected);
        });

        viewPerms.forEach(perm => {
            const isGranted = hasPermission(perm.code);
            
            if (allWillBeChecked) {
                // All are checked - toggle to unchecked (revoke granted ones, unmark non-granted ones)
                if (isGranted) {
                    newSelected.add(perm.code); // Mark for revoke
                } else {
                    newSelected.delete(perm.code); // Unmark for grant
                }
            } else {
                // Not all are checked - toggle to checked (grant all)
                if (isGranted) {
                    newSelected.delete(perm.code); // Keep granted
                } else {
                    newSelected.add(perm.code); // Mark for grant
                }
            }
        });

        setSelectedPermissions(newSelected);
    };

    const handleGrantModifyAll = (area) => {
        const areaPermissions = groupedPermissions[area] || [];
        const newSelected = new Set(selectedPermissions);

        const modifyPerms = areaPermissions.filter(perm => perm.code.includes('.modify'));

        // Check if all modify perms will be checked (granted or marked for grant)
        const allWillBeChecked = modifyPerms.every(perm => {
            const isGranted = hasPermission(perm.code);
            const isSelected = newSelected.has(perm.code);
            return (isGranted && !isSelected) || (!isGranted && isSelected);
        });

        modifyPerms.forEach(perm => {
            const isGranted = hasPermission(perm.code);
            
            if (allWillBeChecked) {
                // All are checked - toggle to unchecked (revoke granted ones, unmark non-granted ones)
                if (isGranted) {
                    newSelected.add(perm.code); // Mark for revoke
                } else {
                    newSelected.delete(perm.code); // Unmark for grant
                }
            } else {
                // Not all are checked - toggle to checked (grant all)
                if (isGranted) {
                    newSelected.delete(perm.code); // Keep granted
                } else {
                    newSelected.add(perm.code); // Mark for grant
                }
            }
        });

        setSelectedPermissions(newSelected);
    };

    const handleGrantActionsAll = (area) => {
        const areaPermissions = groupedPermissions[area] || [];
        const newSelected = new Set(selectedPermissions);

        const actionPerms = areaPermissions.filter(perm =>
            !perm.code.includes('.view') && !perm.code.includes('.modify')
        );

        // Check if all action perms will be checked (granted or marked for grant)
        const allWillBeChecked = actionPerms.every(perm => {
            const isGranted = hasPermission(perm.code);
            const isSelected = newSelected.has(perm.code);
            return (isGranted && !isSelected) || (!isGranted && isSelected);
        });

        actionPerms.forEach(perm => {
            const isGranted = hasPermission(perm.code);
            
            if (allWillBeChecked) {
                // All are checked - toggle to unchecked (revoke granted ones, unmark non-granted ones)
                if (isGranted) {
                    newSelected.add(perm.code); // Mark for revoke
                } else {
                    newSelected.delete(perm.code); // Unmark for grant
                }
            } else {
                // Not all are checked - toggle to checked (grant all)
                if (isGranted) {
                    newSelected.delete(perm.code); // Keep granted
                } else {
                    newSelected.add(perm.code); // Mark for grant
                }
            }
        });

        setSelectedPermissions(newSelected);
    };

    const handleSelectAll = () => {
        const newSelected = new Set();

        // For each permission, if not already granted, add to selection (to grant it)
        // If already granted, don't add to selection (keep it granted)
        allPermissions.forEach(perm => {
            const isGranted = hasPermission(perm.code);
            if (!isGranted) {
                newSelected.add(perm.code);
            }
        });

        setSelectedPermissions(newSelected);
    };

    const handleUnselectAll = () => {
        const newSelected = new Set();

        // For each permission, if already granted, add to selection (to revoke it)
        // If not granted, don't add to selection (keep it not granted)
        allPermissions.forEach(perm => {
            const isGranted = hasPermission(perm.code);
            if (isGranted) {
                newSelected.add(perm.code);
            }
        });

        setSelectedPermissions(newSelected);
    };

    const handleUpdatePermissions = async () => {
        try {
            setSaving(true);
            setAlert(null);

            // Determine which permissions to grant and revoke
            // selectedPermissions contains ONLY the changes:
            // - For already granted permissions: being selected means "mark for revoke"
            // - For non-granted permissions: being selected means "mark for grant"
            const toGrant = [];
            const toRevoke = [];

            selectedPermissions.forEach(permCode => {
                const isGranted = hasPermission(permCode);

                if (isGranted) {
                    // Already granted and selected = mark for revoke
                    toRevoke.push(permCode);
                } else {
                    // Not granted and selected = mark for grant
                    toGrant.push(permCode);
                }
            });

            // Check if user would have no permissions
            const finalPermissionCount = userPermissions.length + toGrant.length - toRevoke.length;
            if (finalPermissionCount === 0 && !user.is_superuser) {
                if (!window.confirm('Warning: This user will have no permissions and will not be able to access any features. Are you sure you want to continue?')) {
                    setSaving(false);
                    return;
                }
            }

            // Execute changes
            if (toRevoke.length > 0) {
                await permissionsAPI.revokePermissions(userId, toRevoke);
            }

            if (toGrant.length > 0) {
                await permissionsAPI.grantPermissions(userId, toGrant, '');
            }

            const message = [];
            if (toGrant.length > 0) message.push(`Granted ${toGrant.length}`);
            if (toRevoke.length > 0) message.push(`Revoked ${toRevoke.length}`);

            setAlert({
                variant: 'success',
                message: `Successfully updated permissions: ${message.join(', ')}`
            });

            // Reload user permissions
            await loadData();
            setSelectedPermissions(new Set());
        } catch (error) {
            setAlert({ variant: 'danger', message: error.message || 'Failed to update permissions' });
        } finally {
            setSaving(false);
        }
    };

    const handleRevokePermission = async (permissionCode) => {
        try {
            setSaving(true);
            setAlert(null);

            await permissionsAPI.revokePermissions(userId, [permissionCode]);

            setAlert({ variant: 'success', message: 'Permission revoked successfully' });

            // Reload user permissions
            await loadData();
        } catch (error) {
            setAlert({ variant: 'danger', message: error.message || 'Failed to revoke permission' });
        } finally {
            setSaving(false);
        }
    };

    if (permissionsLoading || loading) {
        return (
            <Container className="py-3">
                <div className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            </Container>
        );
    }

    if (!hasCurrentUserPermission('users.modify')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to manage user permissions.
                </Alert>
            </Container>
        )
    }

    if (!user) {
        return (
            <Container className="py-3">
                <Alert variant="danger">User not found</Alert>
            </Container>
        );
    }

    return (
        <Container className="py-3">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1>Manage Permissions for {user.username}</h1>
                <Button variant="outline-primary" onClick={() => navigate(-1)}>
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to List
                </Button>
            </div>

            {alert && (
                <Alert
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                    dismissible
                >
                    {alert.message}
                </Alert>
            )}

            {/* Permissions Management */}
            <div>
                <div 
                    className="d-flex justify-content-end mb-3 pb-2"
                    style={{
                        borderBottom: '2px solid var(--bs-primary)'
                    }}
                >
                    <Button
                        variant="primary"
                        size="sm"
                        className="me-2"
                        onClick={handleSelectAll}
                    >
                        Grant All
                    </Button>
                    <Button
                        variant="danger"
                        size="sm"
                        onClick={handleUnselectAll}
                    >
                        Revoke All
                    </Button>
                </div>

                {Object.keys(groupedPermissions).sort().map(area => (
                    <div key={area} className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5 className="text-uppercase mb-0 fw-semibold" style={{ color: 'var(--bs-primary)' }}>{area}</h5>
                            <div>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => handleGrantViewAll(area)}
                                >
                                    Read
                                </Button>
                                <Button
                                    variant="outline-warning"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => handleGrantModifyAll(area)}
                                >
                                    Write
                                </Button>
                                <Button
                                    variant="outline-success"
                                    size="sm"
                                    onClick={() => handleGrantActionsAll(area)}
                                >
                                    Execute
                                </Button>
                            </div>
                        </div>
                        <Table bordered hover size="sm">
                            <tbody>
                                {groupedPermissions[area].map(perm => {
                                    const alreadyGranted = hasPermission(perm.code);
                                    const isSelected = selectedPermissions.has(perm.code);
                                    // Checkbox is checked if: currently granted AND not in revoke list, OR newly selected
                                    const isChecked = alreadyGranted ? !isSelected : isSelected;

                                    return (
                                        <tr key={perm.code}>
                                            <td style={{ width: '40px' }}>
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={() => handleTogglePermission(perm.code)}
                                                />
                                            </td>
                                            <td>
                                                <span>{perm.description}</span>
                                                {alreadyGranted && !isSelected && (
                                                    <Badge bg="success" className="ms-2">✓ Granted</Badge>
                                                )}
                                                {alreadyGranted && isSelected && (
                                                    <Badge bg="warning" className="ms-2">⚠ Will Revoke</Badge>
                                                )}
                                                {!alreadyGranted && isSelected && (
                                                    <Badge bg="info" className="ms-2">+ Will Grant</Badge>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                ))}

                {selectedPermissions.size > 0 && (
                    <div
                        className="d-flex justify-content-between align-items-center mt-4 p-3 rounded"
                        style={{
                            backgroundColor: 'var(--bs-body-bg)',
                            border: '2px solid var(--bs-primary)'
                        }}
                    >
                        <div>
                            <strong>{selectedPermissions.size}</strong> change(s) pending
                        </div>
                        <div>
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setSelectedPermissions(new Set());
                                    setAlert(null);
                                }}
                                className="me-2"
                                disabled={saving}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleUpdatePermissions}
                                disabled={saving}
                            >
                                {saving ? 'Updating...' : 'Update Permissions'}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Container>
    );
}

export default UserPermissionManagementScreen;
